import { FC, ReactElement, useMemo, useState } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  Button,
  TableRow,
  TableCell,
  styled,
} from "@mui/material";
import CollapsibleTable from "../collapsible-table";
import { columns as importExportColumns } from "@/columns/COMMON/import-export";
import { columns as tariffColumns } from "@/columns/COMMON/tariff-codes";
import { columns as vendorsColumns } from "@/columns/COMMON/vendors";
import {
  COUNTRIES_TOTAL_FIELDS,
  TARIFF_CODES_TOTAL_FIELDS,
  VENDOS_TOTAL_FIELDS,
} from "@/constants";
import { getFormattedValue, handleCSVDownload } from "@/utils";
import { responsiveFontSizes, pxToRem } from "@/utils/responsive";
import { VendorsTable, NoData } from "@/components";

// Styles from VendorsCard with added height constraints
const VendorTableWrapper = styled(Box)(({ theme }) => ({
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  maxHeight: 400,
  [theme.breakpoints.down("md")]: {
    maxHeight: 360,
  },
  "& .MuiTable-root": {
    borderCollapse: "collapse",
    "& .MuiTableCell-root": {
      border: 0,
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: `${pxToRem(7)} ${pxToRem(12)} !important`,
      borderBlock: "1px solid #E6E7E7",
    },
  },
}));

type TabType = "country" | "tariff" | "vendor";

interface UnifiedAnalyticsCardProps {
  importExportData: {
    exports: IExport[];
    _id: string;
  }[];
  importExportFooterData: Record<CountryFields, string | number>;
  tariffData: ITariff[];
  tariffIsLoading: boolean;
  vendorsData: IVendor[];
}

const UnifiedAnalyticsCard: FC<UnifiedAnalyticsCardProps> = ({
  importExportData,
  importExportFooterData,
  tariffData,
  tariffIsLoading,
  vendorsData,
}): ReactElement => {
  const [activeTab, setActiveTab] = useState<TabType>("country");

  // --- Logic for Country of Origin (ImportExport) ---
  const {
    topLevel: importExportTopLevel,
    htsData: importExportHtsData,
    totalUniqueHtsCodes,
  } = useMemo(() => {
    const topLevel: CountryData[] = [];
    const htsData: Record<
      string,
      (IHtsNumber & { exportCountry: string })[]
    > = {};
    const totalUniqueHtsCodes = new Set<string>();

    if (importExportData.length > 0) {
      importExportData.forEach((country) => {
        const topLevelDistinctSupplierValues = new Set();
        const topLevelDistinctImportValues = new Set();
        const uniqueHtsCodesForCountry = new Set<string>();
        let topLevelCustomValue = 0;
        let topLevelCustomDuty = 0;
        let topLevelEffectiveDutyRate = 0;
        let topLevelAvgShipmentValue = 0;
        let topLevelDutySaved = 0;
        let totalHtsCount = 0;

        htsData[country._id] = [];

        country.exports.forEach((exportItem) => {
          exportItem.htsNumbers.forEach((hts) => {
            topLevelCustomValue += hts.customValue;
            topLevelCustomDuty += hts.customDuty;
            topLevelEffectiveDutyRate += hts.effectiveDutyRate;
            topLevelAvgShipmentValue += hts.avgShipmentValue;
            topLevelDutySaved += hts.dutySaved;
            hts.distinctSuppliersValues.forEach((supplier: string) =>
              topLevelDistinctSupplierValues.add(supplier)
            );
            hts.distinctImportsValues.forEach((importValue: string) =>
              topLevelDistinctImportValues.add(importValue)
            );

            uniqueHtsCodesForCountry.add(hts.id);
            totalUniqueHtsCodes.add(hts.id);
            totalHtsCount++;

            htsData[country._id].push({
              ...hts,
              exportCountry: exportItem._id,
            });
          });
        });

        topLevelEffectiveDutyRate =
          totalHtsCount > 0 ? topLevelEffectiveDutyRate / totalHtsCount : 0;

        if (country._id) {
          topLevel.push({
            _id: country._id,
            customValue: topLevelCustomValue,
            customDuty: topLevelCustomDuty,
            distinctImports: topLevelDistinctImportValues.size,
            distinctSuppliers: topLevelDistinctSupplierValues.size,
            effectiveDutyRate: topLevelEffectiveDutyRate,
            avgShipmentValue:
              topLevelDistinctImportValues.size > 0
                ? topLevelCustomValue / topLevelDistinctImportValues.size
                : 0,
            dutySaved: topLevelDutySaved,
            htsCount: uniqueHtsCodesForCountry.size,
          } as CountryData & { htsCount: number });
        }
      });
    }

    return {
      topLevel,
      htsData,
      totalUniqueHtsCodes: totalUniqueHtsCodes.size,
    };
  }, [importExportData]);

  // --- Logic for Tariff Codes ---
  const {
    tariffValue,
    tariffDuty,
    percentOfTotal,
    effectiveDutyRate: tariffEffectiveDutyRate,
    enrichedTariffData,
  } = useMemo(() => {
    if (!tariffData) {
      return {
        tariffValue: 0,
        tariffDuty: 0,
        percentOfTotal: 100,
        effectiveDutyRate: 0,
        enrichedTariffData: [],
      };
    }

    const totalValue = tariffData.reduce(
      (sum, item) => sum + (item.tariffValue || 0),
      0
    );
    const totalDuty = tariffData.reduce(
      (sum, item) => sum + (item.tariffDuty || 0),
      0
    );
    const effectiveDutyRate =
      totalValue > 0 ? (totalDuty / totalValue) * 100 : 0;

    const enriched = tariffData.map((item) => ({
      ...item,
      percentOfTotal:
        totalValue > 0 ? ((item.tariffValue || 0) / totalValue) * 100 : 0,
    }));

    return {
      tariffValue: totalValue,
      tariffDuty: totalDuty,
      percentOfTotal: 100,
      effectiveDutyRate,
      enrichedTariffData: enriched,
    };
  }, [tariffData]);

  // --- Logic for Vendors ---
  const {
    vendorsCustomsDuty,
    vendorsCustomsValue,
    vendorsPercentCustomValue,
  } = useMemo(() => {
    const customsDuty = vendorsData.reduce(
      (prev, { tariffDuty }) => prev + tariffDuty,
      0
    );
    const customsValue = vendorsData.reduce(
      (prev, { tariffValue }) => prev + tariffValue,
      0
    );
    const percentCustomValue = customsValue
      ? (customsDuty / customsValue) * 100
      : 0;

    return {
      vendorsCustomsDuty: customsDuty,
      vendorsCustomsValue: customsValue,
      vendorsPercentCustomValue: percentCustomValue,
    };
  }, [vendorsData]);

  // --- Render Helpers ---

  const renderDownloadButton = () => {
    if (activeTab === "country") return null;

    const handleClick = () => {
      if (activeTab === "tariff") {
        if (!tariffData) return;
        const headers = [
          "HTS Code",
          "Goods Value",
          "Customs Duty",
          "Effective Duty Rate (%)",
        ];
        const csvData = tariffData.map((r) => [
          r.htsNumber,
          r.tariffValue ?? 0,
          r.tariffDuty ?? 0,
          (r.tariffValue ?? 0) > 0
            ? (((r.tariffDuty ?? 0) / (r.tariffValue ?? 1)) * 100).toFixed(2)
            : "0.00",
        ]);
        handleCSVDownload(headers, csvData, "tariff_codes.csv");
      } else if (activeTab === "vendor") {
        const headers = [
          "Manufacturer",
          "Manufacturer ID",
          "Customs Value",
          "Customs Duty",
          "Effective Duty Rate (%)",
        ];
        const csvData = vendorsData.map((r) => [
          r.manufacturerName ?? r.manufacturer,
          r.manufacturer,
          `$${Number(r.tariffValue ?? 0)}`,
          `$${Number(r.tariffDuty ?? 0)}`,
          (r.tariffValue ?? 0) > 0
            ? (((r.tariffDuty ?? 0) / (r.tariffValue ?? 1)) * 100).toFixed(2)
            : "0.00",
        ]);
        handleCSVDownload(headers, csvData, "manufacturers.csv");
      }
    };

    return (
      <Button size="small" variant="outlined" onClick={handleClick}>
        Download CSV
      </Button>
    );
  };

  const renderContent = () => {
    if (activeTab === "country") {
      if (importExportData.length === 0) {
        return <NoData />;
      }
      return (
        <CollapsibleTable
          columns={importExportColumns}
          countries={importExportTopLevel}
          htsData={importExportHtsData}
          footer={() => (
            <TableRow>
              {COUNTRIES_TOTAL_FIELDS.map((section, index) => {
                const position = importExportColumns[index].align;
                let value: string | number = "";
                let key = "";
                if (section.type === "total") {
                  key = `${section.name}-${index}`;
                  if (index === 1) {
                    value = `${totalUniqueHtsCodes} HTS Codes`;
                  } else {
                    value = section.name;
                  }
                } else {
                  key = `${section.fieldName}-${index}`;
                  value = getFormattedValue(
                    importExportFooterData[section.fieldName],
                    section.symbol ?? "",
                    position
                  );
                }
                return (
                  <TableCell
                    key={`footer-${key}`}
                    sx={{
                      border: "none",
                      background: "#E6ECED",
                      borderBottom: "none",
                      padding: { xs: "3px 4px", md: "5px 6px" },
                      position: "sticky",
                      bottom: "0",
                      zIndex: 2,
                      fontSize: { xs: "10px", md: "11px", lg: "12px" },
                      fontWeight: 500,
                      color: "#030712",
                    }}
                  >
                    {value}
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        />
      );
    }

    if (activeTab === "tariff") {
      return (
        <Box
          sx={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: { xs: 360, md: 400 },
          }}
        >
          <VendorsTable
            columns={tariffColumns()}
            contentIdKey="htsNumber"
            data={enrichedTariffData}
            footerColumns={TARIFF_CODES_TOTAL_FIELDS}
            footerData={{
              tariffValue,
              tariffDuty,
              effectiveDutyRate: tariffEffectiveDutyRate,
              percentOfTotal,
            }}
            isLoading={tariffIsLoading}
            limit={1000}
            page={1}
            total={enrichedTariffData.length}
            fillerRowCount={0}
          />
        </Box>
      );
    }

    if (activeTab === "vendor") {
      return (
        <VendorTableWrapper>
          <VendorsTable
            columns={vendorsColumns}
            contentIdKey="manufacturer"
            data={vendorsData}
            footerColumns={VENDOS_TOTAL_FIELDS}
            footerData={{
              customsDuty: vendorsCustomsDuty,
              customsValue: vendorsCustomsValue,
              percentCustomValue: vendorsPercentCustomValue,
            }}
            isLoading={false}
            limit={10}
            page={1}
            total={10}
            fillerRowCount={0}
          />
        </VendorTableWrapper>
      );
    }

    return null;
  };

  return (
    <Card sx={{ p: { xs: 1.5, md: 2 }, height: "auto", minHeight: "auto" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={0.5}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography
            color="#030712"
            fontWeight={500}
            sx={{
              fontSize: responsiveFontSizes.lg,
              lineHeight: 1.3,
            }}
          >
            Imports by:
          </Typography>
          <Stack direction="row" spacing={1}>
            {[
              { id: "country", label: "Country of Origin" },
              { id: "tariff", label: "Tariff Codes" },
              { id: "vendor", label: "Manufacturers" },
            ].map((pill) => (
              <Box
                key={pill.id}
                onClick={() => setActiveTab(pill.id as TabType)}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: "20px",
                  cursor: "pointer",
                  backgroundColor:
                    activeTab === pill.id ? "#F0FDFD" : "transparent",
                  border:
                    activeTab === pill.id
                      ? "1px solid #0E7090"
                      : "1px solid #E5E7EB",
                  color: activeTab === pill.id ? "#111827" : "#111827",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor:
                      activeTab === pill.id ? "#F0FDFD" : "#F9FAFB",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: responsiveFontSizes.sm,
                    fontWeight: 500,
                  }}
                >
                  {pill.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
        {renderDownloadButton()}
      </Stack>

      <Box mb={2}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: responsiveFontSizes.xs,
          }}
        >
          {activeTab === "country"
            ? "Expand to see rates by code"
            : activeTab === "tariff"
            ? "Top HTS codes by import value"
            : "Analyze your supplier network's import activity by value, duty exposure, and entry volume."}
        </Typography>
      </Box>

      {renderContent()}
    </Card>
  );
};

export default UnifiedAnalyticsCard;


